import { useEffect } from "react";
import { anchorDerivedColors } from "../../utils/color";

/* ══ DYNAMIC THEME SYSTEM ═════════���════════════════════════
   Injects CSS custom properties based on branding settings.
   anchorColor  → --primary/--ink (derived dark tone), --accent/--gold (anchor itself)
   Tonal palette → --tone-0 … --tone-11 (12-step lightness ramp)
   Dark mode    → inverts surface/background tokens
══════════���══════════════════════════════���════════════════ */

export default function useBrandingTheme(branding) {
  // Apply directly to documentElement.style so it always wins over static <style> tags
  useEffect(() => {
    const b = branding || {};
    /* Single anchor colour drives the entire theme */
    const anchor = b.anchorColor || "#C9A84C";
    const { primary, accent, palette } = anchorDerivedColors(anchor);
    const isDark  = b.themeMode === "dark" ||
      (b.themeMode === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);

    const root = document.documentElement;

    // Set each CSS variable directly — this has highest specificity and survives re-renders
    root.style.setProperty("--primary", primary);
    root.style.setProperty("--accent",  accent);
    root.style.setProperty("--gold",    accent);
    root.style.setProperty("--gold-bg", accent + "14");
    root.style.setProperty("--gold-bd", accent + "55");
    root.style.setProperty("--gold-l",  accent + "33");
    root.style.setProperty("--accent-rgb", ((h)=>{const r=parseInt(h.slice(1,3),16),g=parseInt(h.slice(3,5),16),b=parseInt(h.slice(5,7),16);return`${r},${g},${b}`;})(accent.length===7?accent:"#C9A84C"));

    /* Expose the full 12-step tonal palette as CSS variables --tone-0 … --tone-11 */
    palette.forEach((c,i)=>root.style.setProperty(`--tone-${i}`,c));

    if (isDark) {
      root.style.setProperty("--page",  "#0F0F11");
      root.style.setProperty("--white", "#161618");
      root.style.setProperty("--g50",   "#1C1C1F");
      root.style.setProperty("--g100",  "#242427");
      root.style.setProperty("--g200",  "#343438");
      root.style.setProperty("--g300",  "#4A4A50");
      root.style.setProperty("--g400",  "#7A7A82");
      root.style.setProperty("--g500",  "#9A9AA4");
      root.style.setProperty("--g600",  "#BCBCC6");
      root.style.setProperty("--g700",  "#DCDCE6");
      root.style.setProperty("--ink",   "#F0EFE8");
    } else {
      root.style.setProperty("--page",  "#FAFAF9");
      root.style.setProperty("--white", "#FFFFFF");
      root.style.setProperty("--g50",   "#F5F4F2");
      root.style.setProperty("--g100",  "#ECEAE6");
      root.style.setProperty("--g200",  "#E8E5E0");
      root.style.setProperty("--g300",  "#D4CFC8");
      root.style.setProperty("--g400",  "#A09A94");
      root.style.setProperty("--g500",  "#6B6660");
      root.style.setProperty("--g600",  "#4A4540");
      root.style.setProperty("--g700",  "#2E2A26");
      root.style.setProperty("--ink",   primary);
    }

    document.body.classList.toggle("dark-theme", isDark);

    if (b.favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = b.favicon;
    }
  }); // No dependency array — runs after every render, always in sync
}
